import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  CheckoutReviewsProvider,
  lowProfileManualProductSourceSchema,
  PurchaseCheckoutBlockRenderTarget,
  ShippingOptionRenderTarget,
  ThresholdManualProductSourceSchema,
  UpsellProductDiscountSchema,
} from "./widget";

/**
 * Base schema for all checkout widgets in the collection
 */
const checkoutWidgetBaseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  type: createEnum(CheckoutOptimizationWidgetType),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const baseStandardWidgetSchema = checkoutWidgetBaseSchema.extend({
  enabledGlobally: z.boolean(),
  target: createEnum(PurchaseCheckoutBlockRenderTarget),
});

/**
 * Fulfillment Cutoff Widget schema
 */
const fulfillmentCutoffWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
  text: z.string(),
  backgroundColor: z.string(),
  iconColor: z.string(),
});

export type FulfillmentCutoffWidget = z.infer<
  typeof fulfillmentCutoffWidgetSchema
>;

/**
 * Threshold Upsell Widget schema
 */
const thresholdUpsellWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
  threshold: z.object({ amount: z.number(), currency: createEnum(Currency) }),
  gteThresholdText: z.string(),
  ltThresholdText: z.string(),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  progressBarColor: z
    .object({ filled: z.string().optional(), unfilled: z.string().optional() })
    .optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(ThresholdManualProductSourceSchema).optional(),
  defaultProductDiscount: UpsellProductDiscountSchema.optional(),
});

export type ThresholdUpsellWidget = z.infer<typeof thresholdUpsellWidgetSchema>;

/**
 * Low-profile Product Upsell Widget schema
 */
const lowProfileProductUpsellWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(lowProfileManualProductSourceSchema).optional(),
});

export type LowProfileProductUpsellWidget = z.infer<
  typeof lowProfileProductUpsellWidgetSchema
>;

/**
 * Content Block Widget schema
 */
const contentBlockWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
  sections: z
    .array(
      z.object({
        _id: z.string(),
        title: z.string().nullish(),
        description: z.string().nullish(),
        imageUrl: z.string().nullish(),
      }),
    )
    .max(5),
});

export type ContentBlockWidget = z.infer<typeof contentBlockWidgetSchema>;

/**
 * Image Block Widget schema
 */
const imageBlockWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.IMAGE_BLOCK),
  imageUrl: z.string().nullish(),
  imageWidthPx: z.coerce.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});

export type ImageBlockWidget = z.infer<typeof imageBlockWidgetSchema>;

/**
 * Shipping Option Content Widget schema
 * Renders within shipping option items in checkout
 */
export const shippingOptionContentWidgetSchema =
  checkoutWidgetBaseSchema.extend({
    type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
    target: createEnum(ShippingOptionRenderTarget),
    title: z.string(),
    description: z.string(),
    imageUrl: z.string().nullish(),
    backgroundColor: z.string(),
  });
export interface ShippingOptionContentWidget
  extends z.infer<typeof shippingOptionContentWidgetSchema> {}

/**
 * Shipping Option Image Widget schema
 * Renders an image within shipping option items in checkout
 */
export const shippingOptionImageWidgetSchema = checkoutWidgetBaseSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE),
  target: createEnum(ShippingOptionRenderTarget),
  imageUrl: z.string().nullish(),
  imageWidth: z.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});
export interface ShippingOptionImageWidget
  extends z.infer<typeof shippingOptionImageWidgetSchema> {}

/**
 * Failed Discount Rescue Widget schema
 *
 * Renders nothing visible in checkout. Subscribes (via the long-poll RPC) to
 * failed-discount-attempt events for the current checkout token. When one
 * arrives, shows a "we've got you covered" rescue UI with a Claim button
 * that applies the merchant-configured discount via
 * `useApplyDiscountCodeChange`.
 *
 * `discountId` references a `Discount` document — the same first-class
 * Discount entity that Spotlights, RECOVER, marketing agents, and other
 * Redo features reference. The actual code is generated on demand at claim
 * time via `getCodeForDiscount` from redo/discounts/service. Optional
 * because newly-created widgets may not have a discount selected yet; the
 * runtime claim handler treats a missing discountId as `widget-misconfigured`.
 */
const failedDiscountRescueWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE),
  discountId: zExt.objectId().optional(),
  text: z.string(),
  buttonText: z.string(),
});

export type FailedDiscountRescueWidget = z.infer<
  typeof failedDiscountRescueWidgetSchema
>;

/**
 * Product Reviews Widget schema
 *
 * Renders a carousel of reviews for products in the buyer's cart, sourced
 * from the merchant's configured reviews provider (e.g. Judge.me). Reviews
 * are fetched at render time — no review content is persisted on the widget.
 * If the provider integration is missing, disabled, or returns no qualifying
 * reviews, the widget renders nothing.
 */
const productReviewsWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.PRODUCT_REVIEWS),
  provider: createEnum(CheckoutReviewsProvider),
  minRating: z.number().int().min(1).max(5),
  display: z.object({
    showProductName: z.boolean(),
    showReviewTitle: z.boolean(),
    showReviewBody: z.boolean(),
    showReviewerName: z.boolean(),
    showVerifiedBadge: z.boolean(),
  }),
});

export type ProductReviewsWidget = z.infer<typeof productReviewsWidgetSchema>;

/**
 * Union of all shipping option widget types
 */
export const shippingOptionWidgetSchema = z.discriminatedUnion("type", [
  shippingOptionContentWidgetSchema,
  shippingOptionImageWidgetSchema,
]);
export type ShippingOptionWidget = z.infer<typeof shippingOptionWidgetSchema>;

/**
 * Discriminated union of all checkout widget types
 */
export const checkoutWidgetSchema = z.discriminatedUnion("type", [
  fulfillmentCutoffWidgetSchema,
  thresholdUpsellWidgetSchema,
  lowProfileProductUpsellWidgetSchema,
  contentBlockWidgetSchema,
  imageBlockWidgetSchema,
  shippingOptionContentWidgetSchema,
  shippingOptionImageWidgetSchema,
  failedDiscountRescueWidgetSchema,
  productReviewsWidgetSchema,
]);

export type CheckoutWidget = z.infer<typeof checkoutWidgetSchema>;

export const checkoutWidgetWithAssignmentsSchema = z.intersection(
  checkoutWidgetSchema,
  z.object({
    assignedExperienceIds: z.array(zExt.objectId()),
    assignedExperienceNames: z.array(z.string()),
  }),
);

export type CheckoutWidgetWithAssignments = z.infer<
  typeof checkoutWidgetWithAssignmentsSchema
>;
